import type { AuditObjectAuditSummary, AuditScoreType } from "@ots-frontend/ots-data";
import { entryQuantity, flattenEntries, isActivityTicket, isUnvalidated, TANK_DROPS_KEY } from "./activity-entries";
import { secondScadaQty } from "./activity-flags";
import type { ActivityDriver, ActivityEntry, ActivityStats } from "./activity-types";

/** The legacy aggregate: the weakest audit among some entries, which is what a driver or shift is only as good as. */
export const lowestAudit = (entries: ActivityEntry[], scoreType: AuditScoreType): AuditObjectAuditSummary | undefined => {
  let lowest: AuditObjectAuditSummary | undefined;
  for (const { audit } of entries) {
    const value = audit?.confidences[scoreType];
    if (!audit?.hasAuditRun || value == null) continue;
    // Compared as reported: one audit service reports every ticket on the same scale.
    if (!lowest || audit.blockingFindingCount > lowest.blockingFindingCount
      || (audit.blockingFindingCount === lowest.blockingFindingCount && value < (lowest.confidences[scoreType] ?? 1))) {
      lowest = audit;
    }
  }
  return lowest;
};


/** Day-level numbers shown in the summary strip; mirrors the legacy viewer's stats footer. */
export const summarizeDriverActivity = (drivers: ActivityDriver[]): ActivityStats => {
  const stats: ActivityStats = {
    drivers: drivers.filter((driver) => driver.key !== TANK_DROPS_KEY).length, loads: 0, quantity: 0, revenue: 0, cost: 0, attention: 0, unmatched: 0, unvalidated: 0,
    scada: { calculated: 0, known: 0 },
    auditBlocked: 0,
    subcontractor: 0,
  };
  const scadaPickups = new Set<string>();

  for (const entry of flattenEntries(drivers)) {
    if (!isActivityTicket(entry)) continue;
    stats.loads++;
    stats.quantity += entryQuantity(entry);
    stats.quantityUom ??= entry.pickup?.quantityUnitOfMeasure ?? entry.delivery?.quantityUnitOfMeasure;
    stats.revenue += entry.revenue ?? 0;
    stats.cost += entry.cost ?? 0;
    stats.subcontractor += entry.subcontractorCost ?? 0;
    if (entry.needsAttention) stats.attention++;
    if (entry.kind !== "load") stats.unmatched++;
    if ((entry.audit?.blockingFindingCount ?? 0) > 0) stats.auditBlocked++;
    if (isUnvalidated(entry)) stats.unvalidated++;

    const pickup = entry.tta ? entry.pickup : undefined;
    if (pickup && !scadaPickups.has(pickup.id)) {
      scadaPickups.add(pickup.id);
      const hasScadaQty = pickup.scadaQty != null || secondScadaQty(pickup) != null;
      if (pickup.scadaQtyCalcState != null || hasScadaQty) {
        stats.scada.known++;
        if (pickup.scadaQtyCalcState === "CALCULATED" || hasScadaQty) stats.scada.calculated++;
      }
    }
  }
  return stats;
};
